import mixpanel from "mixpanel-browser";

// Mixpanel token from the installation wizard
const MIXPANEL_TOKEN = "8a972e013e01c83d7f1842314fa06e81";

// Track if Mixpanel has been initialized
let isInitialized = false;

/**
 * Initialize Mixpanel with configuration
 * Should be called once at app startup
 */
export function initMixpanel(): void {
  if (isInitialized) {
    return;
  }

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: process.env.NODE_ENV === "development",
    track_pageview: true,
    persistence: "localStorage",
    autocapture: true,
    record_sessions_percent: 100,
  });

  isInitialized = true;

  if (process.env.NODE_ENV === "development") {
    console.log("Mixpanel initialized");
  }
}

/**
 * Identify a user in Mixpanel
 * Call this when a user logs in or signs up
 */
export function identifyUser(
  userId: string | number,
  properties?: {
    email?: string;
    name?: string;
    role?: string;
    [key: string]: unknown;
  }
): void {
  if (!isInitialized) {
    console.warn("Mixpanel not initialized. Call initMixpanel() first.");
    return;
  }

  mixpanel.identify(String(userId));

  if (properties) {
    const peopleProps: Record<string, unknown> = {};

    if (properties.email) {
      peopleProps.$email = properties.email;
    }
    if (properties.name) {
      peopleProps.$name = properties.name;
    }

    // Add any additional properties
    Object.entries(properties).forEach(([key, value]) => {
      if (key !== "email" && key !== "name") {
        peopleProps[key] = value;
      }
    });

    mixpanel.people.set(peopleProps);
  }
}

/**
 * Reset Mixpanel user identity
 * Call this when a user logs out
 */
export function resetUser(): void {
  if (!isInitialized) {
    return;
  }
  mixpanel.reset();
}

/**
 * Track Sign Up event
 */
export function trackSignUp(properties: {
  user_id: string | number;
  email: string;
  signup_method: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}): void {
  if (!isInitialized) return;
  mixpanel.track("Sign Up", properties);
}

/**
 * Track Sign In event
 */
export function trackSignIn(properties: {
  user_id: string | number;
  login_method: string;
  success: boolean;
}): void {
  if (!isInitialized) return;
  mixpanel.track("Sign In", properties);
}

/**
 * Track Page View event
 */
export function trackPageView(properties: {
  page_url: string;
  page_title: string;
  user_id?: string | number;
}): void {
  if (!isInitialized) return;
  mixpanel.track("Page View", properties);
}

/**
 * Track Search event
 */
export function trackSearch(properties: {
  search_query: string;
  user_id?: string | number;
  results_count?: number;
}): void {
  if (!isInitialized) return;
  mixpanel.track("Search", properties);
}

/**
 * Track Error event
 */
export function trackError(properties: {
  error_type: string;
  error_message: string;
  error_code?: string;
  page_url?: string;
  user_id?: string | number;
}): void {
  if (!isInitialized) return;
  mixpanel.track("Error", properties);
}

/**
 * Track Purchase event
 */
export function trackPurchase(properties: {
  user_id: string | number;
  transaction_id: string;
  revenue: number;
  currency: string;
}): void {
  if (!isInitialized) return;
  mixpanel.track("Purchase", properties);
}

/**
 * Track Conversion event
 */
export function trackConversion(properties: {
  conversion_type: string;
  conversion_value?: number;
}): void {
  if (!isInitialized) return;
  mixpanel.track("Conversion", {
    "Conversion Type": properties.conversion_type,
    "Conversion Value": properties.conversion_value,
  });
}

/**
 * Track a custom event
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  if (!isInitialized) return;
  mixpanel.track(eventName, properties);
}

// Export the mixpanel instance for advanced usage
export { mixpanel };

export default {
  initMixpanel,
  identifyUser,
  resetUser,
  trackSignUp,
  trackSignIn,
  trackPageView,
  trackSearch,
  trackError,
  trackPurchase,
  trackConversion,
  trackEvent,
};
